<script setup>
import { ref, watch } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';

const props = defineProps({
    openTrips: Object,
    featuredTrips: Array,
    destinations: Array,
    setting: Object,
    seoData: Object,
});

const filterDestination = ref(route().params.destination || '');

const handleFilter = () => {
    router.get(route('open-trip.index'), {
        destination: filterDestination.value
    }, {
        preserveState: true,
        preserveScroll: true,
        only: ['openTrips']
    });
};

const clearFilter = () => {
    filterDestination.value = '';
    handleFilter();
};

const scrollToTrips = () => {
    const el = document.getElementById('trips');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
};
</script>

<template>
    <FrontLayout :setting="setting" :title="seoData?.title">
        <Head>

            <meta name="description" :content="seoData?.meta_description" />
            <meta name="keywords" :content="seoData?.meta_keywords" />
            <link v-if="seoData?.canonical_url" rel="canonical" :href="seoData?.canonical_url" />
            <link v-if="seoData?.prev_url" rel="prev" :href="seoData?.prev_url" />
            <link v-if="seoData?.next_url" rel="next" :href="seoData?.next_url" />
            
            <meta property="og:title" :content="seoData?.og_title" />
            <meta property="og:description" :content="seoData?.og_description" />
            <meta property="og:image" :content="seoData?.og_image" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:url" :content="seoData?.og_url" />
            <meta property="og:type" :content="seoData?.og_type" />
            <meta property="og:site_name" content="Gotransport Banyuwangi" />
            
            <meta name="twitter:title" :content="seoData?.twitter_title" />
            <meta name="twitter:description" :content="seoData?.twitter_description" />
            <meta name="twitter:image" :content="seoData?.twitter_image" />
        </Head>

        <!-- Hero Section -->
        <PageHero 
            title="Open Trip Wisata" 
            subtitle="Jelajahi destinasi impian dengan paket trip terbaik dan terpercaya. Nikmati pengalaman tak terlupakan bersama kami!"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Open Trip' }
            ]"
        />

        <!-- Featured Trips -->
        <section v-if="featuredTrips?.length > 0" class="py-8 md:py-10 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="text-center mb-8">
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">Trip Unggulan</h3>
                    <p class="text-gray-600 dark:text-gray-400">Destinasi terpopuler pilihan traveler</p>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="trip in featuredTrips" :key="trip.id" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col group hover:shadow-lg transition-shadow">
                        <!-- Trip Image -->
                        <div v-if="trip.image" class="relative overflow-hidden">
                            <Link :href="`/trip-wisata-banyuwangi/${trip.slug}`">
                                <img :src="`/storage/open-trips/${trip.image}`" 
                                     :alt="trip.title" 
                                     class="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                                     loading="lazy">
                            </Link>
                            <div class="absolute top-2 left-2">
                                <span class="inline-block bg-yellow-500 text-gray-900 px-3 py-1 text-xs font-bold rounded">
                                    <i class="fa fa-star mr-1"></i>Unggulan
                                </span>
                            </div>
                        </div>
                        
                        <div class="p-4 flex flex-col flex-grow">
                            <div v-if="trip.destination" class="mb-3">
                                <span class="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 text-xs font-medium rounded-full">
                                    <i class="fa fa-map-marker mr-1"></i>{{ trip.destination }}
                                </span>
                            </div>
                            
                            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                <Link :href="`/trip-wisata-banyuwangi/${trip.slug}`" class="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                    {{ trip.title }}
                                </Link>
                            </h3>
                            
                            <p v-if="trip.description" class="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow line-clamp-3">
                                {{ trip.description.replace(/<[^>]*>?/gm, '') }}
                            </p>
                                
                            <div class="mt-auto">
                                <Link :href="`/trip-wisata-banyuwangi/${trip.slug}`" 
                                   class="block w-full text-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors rounded-lg">
                                    <i class="fa fa-eye mr-1"></i>Lihat Detail
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Filter Section -->
        <section class="py-12 bg-gray-50 dark:bg-gray-900" id="trips">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="max-w-4xl mx-auto">
                    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-xl p-4">
                        <div class="mb-6">
                            <div class="flex items-center gap-4">
                                <div class="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                    <i class="fa fa-map text-blue-600 dark:text-blue-400 text-xl"></i>
                                </div>
                                <div>
                                    <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-1">Temukan Destinasi Impian Anda</h2>
                                    <p class="text-sm text-gray-600 dark:text-gray-400">Pilih destinasi wisata terbaik di Banyuwangi</p>
                                </div>
                            </div>
                        </div>
                        
                        <div class="space-y-4">
                            <div class="flex flex-col md:flex-row gap-3">
                                <div class="flex-1 relative">
                                    <div class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10">
                                        <i class="fa fa-compass"></i>
                                    </div>
                                    <select v-model="filterDestination" @change="handleFilter"
                                            class="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                                        <option value="">🌟 Jelajahi Semua Destinasi Banyuwangi</option>
                                        <option v-for="dest in destinations" :key="dest" :value="dest">
                                            🏞️ {{ dest }}
                                        </option>
                                    </select>
                                </div>
                                
                                <button @click="handleFilter" 
                                        class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors whitespace-nowrap rounded-lg shadow-blue-200 shadow-lg">
                                    <i class="fa fa-search mr-2"></i>Cari Trip
                                </button>
                            </div>
                            
                            <div v-if="filterDestination" class="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 px-4 py-3 rounded-r-lg">
                                <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    <i class="fa fa-map-pin mr-2 text-blue-600 dark:text-blue-400"></i>
                                    Filter: {{ filterDestination }}
                                </span>
                                <button @click="clearFilter" class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors">
                                    <i class="fa fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Trip List -->
        <section class="py-8 md:py-10 bg-white dark:bg-gray-900">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div v-if="openTrips.data.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div v-for="trip in openTrips.data" :key="trip.id" class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col rounded-xl hover:shadow-xl transition-shadow duration-300">
                        <!-- Trip Image -->
                        <div v-if="trip.image" class="relative group">
                            <Link :href="`/trip-wisata-banyuwangi/${trip.slug}`">
                                <img :src="`/storage/open-trips/${trip.image}`" 
                                     :alt="trip.title" 
                                     class="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                                     loading="lazy">
                            </Link>
                            <div v-if="trip.featured" class="absolute top-2 left-2">
                                <span class="inline-block bg-yellow-500 text-gray-900 px-3 py-1 text-xs font-bold rounded">
                                    <i class="fa fa-star mr-1"></i>Unggulan
                                </span>
                            </div>
                        </div>
                        
                        <div class="p-4 flex flex-col flex-grow">
                            <div v-if="trip.destination" class="mb-3">
                                <span class="inline-block bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-3 py-1 text-xs font-medium rounded-full">
                                    <i class="fa fa-map-marker mr-1"></i>{{ trip.destination }}
                                </span>
                            </div>
                            
                            <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                <Link :href="`/trip-wisata-banyuwangi/${trip.slug}`" class="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                    {{ trip.title }}
                                </Link>
                            </h3>
                            
                            <p v-if="trip.description" class="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow line-clamp-3">
                                {{ trip.description.replace(/<[^>]*>?/gm, '') }}
                            </p>
                                
                            <div class="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700">
                                <Link :href="`/trip-wisata-banyuwangi/${trip.slug}`" 
                                   class="block w-full text-center px-4 py-2.5 bg-gray-50 dark:bg-gray-700 hover:bg-blue-600 dark:hover:bg-blue-600 hover:text-white text-gray-700 dark:text-gray-200 font-bold transition-all rounded-lg group">
                                    Lihat Detail <i class="fa fa-arrow-right ml-1 group-hover:translate-x-1 transition-transform"></i>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div v-else class="col-span-full">
                    <div class="text-center py-16 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl dashed border-2">
                        <i class="fa fa-search text-gray-300 dark:text-gray-600 text-6xl mb-4"></i>
                        <h4 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Tidak ada trip ditemukan</h4>
                        <p class="text-gray-500 dark:text-gray-400 mb-6">Coba ubah filter pencarian atau lihat semua trip yang tersedia</p>
                        <button v-if="filterDestination" @click="clearFilter" 
                           class="inline-block px-6 py-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-medium transition-colors rounded-lg">
                            <i class="fa fa-refresh mr-2"></i>Reset Filter
                        </button>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="openTrips.links && openTrips.data.length > 0" class="mt-10 flex justify-center">
                     <div class="flex flex-wrap gap-2">
                        <Link v-for="(link, i) in openTrips.links" 
                            :key="i"
                            :href="link.url"
                            v-html="link.label"
                            :class="[
                                'px-4 py-2 border rounded-md text-sm transition-colors',
                                link.active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
                                !link.url ? 'opacity-50 cursor-not-allowed' : ''
                            ]"
                            :preserve-scroll="true"
                        />
                     </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="py-16 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <div class="max-w-3xl mx-auto text-center">
                    <h3 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">Tidak Menemukan Trip yang Cocok?</h3>
                    <p class="text-gray-600 dark:text-gray-400 mb-8 text-lg">Hubungi kami untuk trip custom sesuai keinginan dan budget Anda!</p>
                    <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a v-if="setting?.setting_no_wa" :href="`https://wa.me/${setting.setting_no_wa}?text=Halo,%20saya%20tertarik%20dengan%20paket%20open%20trip.%20Mohon%20informasi%20lebih%20lanjut.`" 
                           class="inline-flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-600 text-white font-bold transition-transform hover:-translate-y-1 rounded-full shadow-green-200 shadow-lg w-full sm:w-auto justify-center"
                           target="_blank" rel="noopener">
                            <i class="fa fa-whatsapp text-xl"></i>
                            <span>Chat WhatsApp</span>
                        </a>
                        <Link href="/contact" 
                           class="inline-flex items-center gap-2 px-8 py-3 bg-white border border-gray-200 hover:border-blue-500 hover:text-blue-600 text-gray-700 font-bold transition-all hover:shadow-lg rounded-full w-full sm:w-auto justify-center">
                            <i class="fa fa-envelope"></i>
                            <span>Hubungi Kami</span>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    </FrontLayout>
</template>
